import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, TextField, InputAdornment,
  Snackbar, Alert, Tabs, Tab, Badge, Card, CardContent, CardHeader,
  IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Avatar, Divider, CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EmployeeForm from './EmployeeForm';
import {
  Plus, Search, Users, UserX, MoreVertical,
  Trash2, Edit3, Crown, Shield, User, Settings, UserCheck, Package, Smile
} from 'lucide-react';
import { useRefresh } from '../../contexts/RefreshContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function EmployeeList() {
  const { user } = useAuth();
  const { refreshKey } = useRefresh();

  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployeeForMenu, setSelectedEmployeeForMenu] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);
  const [selectedGroupForMenu, setSelectedGroupForMenu] = useState(null);
  const [deleteGroupDialog, setDeleteGroupDialog] = useState(false);
  const [renameGroupDialog, setRenameGroupDialog] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [emojiDialogOpen, setEmojiDialogOpen] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [allowedEmojis, setAllowedEmojis] = useState([]);
  const [emojiLoading, setEmojiLoading] = useState(false);

  const roleNames = { 1: 'Employee', 2: 'Manager', 3: 'Admin', 4: 'Supervisor', 5: 'Material' };
  const roleIcons = { 1: User, 2: Settings, 3: Crown, 4: Shield, 5: Package };
  const roleColors = { 1: '#666', 2: '#1976d2', 3: '#d32f2f', 4: '#ed6c02', 5: '#2e7d32' };

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [employeesRes, groupsRes] = await Promise.all([
        api.get('/employeespage'),
        api.get('/groups')
      ]);
      setEmployees(employeesRes.data.sort((a, b) => a.id - b.id));
      setGroups(groupsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if ([2, 3, 4].includes(user?.role_id)) {
      fetchData();
    }
  }, [user, refreshKey, fetchData]);

  const handleCreateGroup = async () => {
    try {
      await api.post('/groups');
      showSnackbar('New group created successfully!');
      fetchData();
    } catch (error) {
      console.error("Error creating group:", error);
      showSnackbar(error.response?.data?.msg || 'Error creating group', 'error');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupForMenu) return;
    try {
      await api.delete(`/groups/${selectedGroupForMenu.id}`);
      showSnackbar(`Group "${selectedGroupForMenu.name}" deleted successfully!`);
      setDeleteGroupDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting group:", error);
      showSnackbar(error.response?.data?.msg || 'Error deleting group', 'error');
    }
    handleCloseGroupMenu();
  };

  const handleRenameGroup = async () => {
    if (!selectedGroupForMenu) return;
    const name = (renameValue || '').trim();
    if (!name) {
      showSnackbar('Enter a valid name', 'warning');
      return;
    }
    try {
      await api.put(`/groups/${selectedGroupForMenu.id}`, { name });
      showSnackbar('Group renamed successfully!');
      setRenameGroupDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error renaming group:", error);
      showSnackbar(error.response?.data?.msg || 'Error renaming group', 'error');
    }
  };

  const handleAssignGroup = async (employeeId, groupId) => {
    try {
      const groupIdToSend = groupId === 'unassign' ? null : groupId;
      await api.put(`/employees/${employeeId}/assign_group`, { group_id: groupIdToSend });
      showSnackbar('Employee moved successfully!');
      fetchData();
    } catch (error) {
      console.error("Error moving employee:", error);
      showSnackbar('Error moving employee', 'error');
    }
    handleCloseMenu();
  };

  const handleNew = () => { setSelected(null); setOpenForm(true); };
  const handleEdit = (emp) => { setSelected(emp); setOpenForm(true); };
  const handleTabChange = (event, newValue) => { setTabValue(newValue); setSearch(''); };

  const handleOpenMenu = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployeeForMenu(employee);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedEmployeeForMenu(null);
  };

  const handleOpenGroupMenu = (event, group) => {
    setGroupMenuAnchor(event.currentTarget);
    setSelectedGroupForMenu(group);
  };
  const handleCloseGroupMenu = () => {
    setGroupMenuAnchor(null);
    setSelectedGroupForMenu(null);
  };

  // ─── Emoji do funcionário ──────────────────────────────────────────────────
  // Backend já tinha GET /emojis e PUT /employees/<id>/emoji (admin/manager);
  // faltava só a UI aqui.
  const openEmojiPicker = async (employee) => {
    handleCloseMenu();
    setEmojiTarget(employee);
    setEmojiDialogOpen(true);
    if (allowedEmojis.length > 0) return;   // carrega a lista uma vez só
    setEmojiLoading(true);
    try {
      const res = await api.get('/emojis');
      setAllowedEmojis(res.data?.emojis || []);
    } catch {
      showSnackbar('Error loading emojis', 'error');
    } finally {
      setEmojiLoading(false);
    }
  };

  const saveEmoji = async (emoji) => {
    if (!emojiTarget) return;
    const previous = emojiTarget.avatar_emoji;
    // Otimista: a lista atualiza na hora e volta atrás se o servidor recusar.
    setEmployees(prev => prev.map(e =>
      e.id === emojiTarget.id ? { ...e, avatar_emoji: emoji } : e
    ));
    setEmojiDialogOpen(false);
    try {
      await api.put(`/employees/${emojiTarget.id}/emoji`, { emoji });
      showSnackbar(`Emoji updated for ${emojiTarget.name}`);
    } catch (error) {
      setEmployees(prev => prev.map(e =>
        e.id === emojiTarget.id ? { ...e, avatar_emoji: previous } : e
      ));
      showSnackbar(error?.response?.data?.msg || 'Error saving emoji', 'error');
    } finally {
      setEmojiTarget(null);
    }
  };

  const activeEmployees = employees.filter(emp => emp.status === 'active');
  const inactiveEmployees = employees.filter(emp => emp.status === 'inactive');

  const getFilteredAndGrouped = () => {
    const currentList = tabValue === 0 ? activeEmployees : inactiveEmployees;
    const query = search.toLowerCase();
    const filtered = currentList.filter(e =>
      e.name.toLowerCase().includes(query) ||
      e.email.toLowerCase().includes(query)
    );

    const unassigned = filtered.filter(e => !e.group_id);
    const grouped = groups.map(g => ({
      ...g,
      employees: filtered.filter(e => e.group_id === g.id)
    }));

    return { unassigned, grouped };
  };

  const { unassigned, grouped } = getFilteredAndGrouped();

  if (![2, 3, 4].includes(user?.role_id)) {
    return (
      <Box p={2} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            You don't have permission to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const EmployeeItem = ({ employee }) => {
    const RoleIcon = roleIcons[employee.role_id] || User;
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            borderColor: '#1976d2',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          },
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer'
        }}
      >
     <Avatar
  sx={{
    bgcolor: employee.avatar_emoji ? 'transparent' : roleColors[employee.role_id],
    mr: 2,
    width: 40,
    height: 40,
    fontSize: '1.4rem',
    border: employee.avatar_emoji ? '1px solid #e0e0e0' : 'none',
  }}
>
  {employee.avatar_emoji ? employee.avatar_emoji : <RoleIcon size={20} />}
</Avatar>

        <Box sx={{ flexGrow: 1 }} onClick={() => handleEdit(employee)}>
          <Typography variant="body1" fontWeight="600" color="text.primary">
            {employee.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={roleNames[employee.role_id]}
              size="small"
              sx={{
                bgcolor: `${roleColors[employee.role_id]}15`,
                color: roleColors[employee.role_id],
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {employee.email}
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMenu(e, employee);
          }}
          sx={{
            bgcolor: 'rgba(0,0,0,0.04)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
          }}
        >
          <MoreVertical size={18} />
        </IconButton>
      </Paper>
    );
  };

  const GroupHeader = ({ group, employeeCount }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      bgcolor: '#f8f9fa',
      borderRadius: '8px 8px 0 0'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Users size={20} color="#1976d2" />
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {group ? group.name : 'Unassigned'}
        </Typography>
        <Badge
          badgeContent={employeeCount}
          color="primary"
          sx={{ ml: 1 }}
        />
      </Box>

      {group && (
        <IconButton
          size="small"
          onClick={(e) => handleOpenGroupMenu(e, group)}
          sx={{
            bgcolor: 'rgba(0,0,0,0.04)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
          }}
        >
          <MoreVertical size={16} />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Box p={1}>
      <Box sx={{
        pb: 1,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1,
        maxWidth: 1000,
        margin: '0 auto'
      }}>
        <TextField
          size='small'
          placeholder={`Search in ${tabValue === 0 ? 'active' : 'inactive'}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          sx={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="contained"
          onClick={handleCreateGroup}
          startIcon={<Users size={18} />}
          sx={{
            whiteSpace: 'nowrap',
            fontWeight: 'bold',
            background: 'linear-gradient(to right, #d21941ff, #a10d32ff)',
            '&:hover': {
              background: 'linear-gradient(to right, #c01515ff, #8f0000ff)'
            }
          }}
        >
          New Group
        </Button>
        <Button
          variant="contained"
          onClick={handleNew}
          startIcon={<Plus size={18} />}
          sx={{
            whiteSpace: 'nowrap',
            fontWeight: 'bold',
            background: 'linear-gradient(to right, #1976d2, #0d47a1)',
            '&:hover': {
              background: 'linear-gradient(to right, #1565c0, #003c8f)'
            }
          }}
        >
          New Employee
        </Button>
      </Box>

      <Box sx={{
        maxWidth: 1000,
        margin: '0 auto',
        mb: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab
            icon={<UserCheck size={20} />}
            iconPosition="start"
            label={
              <Badge badgeContent={activeEmployees.length} color="primary">
                <Typography sx={{ ml: 1 }}>Active</Typography>
              </Badge>
            }
          />
          <Tab
            icon={<UserX size={20} />}
            iconPosition="start"
            label={
              <Badge badgeContent={inactiveEmployees.length} color="error">
                <Typography sx={{ ml: 1 }}>Inactive</Typography>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {unassigned.length > 0 && (
                  <Card elevation={2} sx={{ borderRadius: 2 }}>
                    <GroupHeader group={null} employeeCount={unassigned.length} />
                    <CardContent sx={{ pt: 1 }}>
                      {unassigned.map(emp => (
                        <EmployeeItem key={emp.id} employee={emp} />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {grouped.map(group => (
                  group.employees.length > 0 && (
                    <Card key={group.id} elevation={2} sx={{ borderRadius: 2 }}>
                      <GroupHeader group={group} employeeCount={group.employees.length} />
                      <CardContent sx={{ pt: 1 }}>
                        {group.employees.map(emp => (
                          <EmployeeItem key={emp.id} employee={emp} />
                        ))}
                      </CardContent>
                    </Card>
                  )
                ))}

                {activeEmployees.length === 0 && (
                  <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Users size={48} color="#ccc" style={{ marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary">
                      No active employees found
                    </Typography>
                  </Paper>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardHeader
                  title="Inactive Employees"
                  sx={{ bgcolor: '#f5f5f5' }}
                  avatar={<UserX size={24} color="#d32f2f" />}
                />
                <CardContent>
                  {inactiveEmployees
                    .filter(e =>
                      e.name.toLowerCase().includes(search.toLowerCase()) ||
                      e.email.toLowerCase().includes(search.toLowerCase())
                    )
                    .map(emp => (
                      <EmployeeItem key={emp.id} employee={emp} />
                    ))
                  }
                  {inactiveEmployees.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography color="text.secondary">
                        No inactive employees.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          </>
        )}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleEdit(selectedEmployeeForMenu)}>
          <Edit3 size={16} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => openEmojiPicker(selectedEmployeeForMenu)}>
          <Smile size={16} style={{ marginRight: 8 }} />
          Change Emoji
        </MenuItem>
        <Divider />
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="caption">Move to:</Typography>
        </MenuItem>
        {groups.map(group => (
          <MenuItem
            key={group.id}
            onClick={() => handleAssignGroup(selectedEmployeeForMenu?.id, group.id)}
            selected={selectedEmployeeForMenu?.group_id === group.id}
          >
            <Users size={16} style={{ marginRight: 8 }} />
            {group.name}
          </MenuItem>
        ))}
        <MenuItem onClick={() => handleAssignGroup(selectedEmployeeForMenu?.id, 'unassign')}>
          <UserX size={16} style={{ marginRight: 8 }} />
          Remove from Group
        </MenuItem>
      </Menu>

      <Dialog
        open={emojiDialogOpen}
        onClose={() => { setEmojiDialogOpen(false); setEmojiTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Emoji for {emojiTarget?.name}
        </DialogTitle>
        <DialogContent dividers>
          {emojiLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : allowedEmojis.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No emoji configured in allowed_emojis.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {allowedEmojis.map(({ emoji, label }) => (
                <IconButton
                  key={emoji}
                  title={label || emoji}
                  onClick={() => saveEmoji(emoji)}
                  sx={{
                    fontSize: '1.6rem',
                    width: 48,
                    height: 48,
                    border: '2px solid',
                    borderColor: emojiTarget?.avatar_emoji === emoji ? 'primary.main' : 'transparent',
                    borderRadius: 2,
                  }}
                >
                  {emoji}
                </IconButton>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEmojiDialogOpen(false); setEmojiTarget(null); }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={handleCloseGroupMenu}
      >
        <MenuItem
          onClick={() => {
            setRenameValue(selectedGroupForMenu?.name || '');
            setRenameGroupDialog(true);
            setGroupMenuAnchor(null);
          }}
        >
          <Edit3 size={16} style={{ marginRight: 8 }} />
          Rename Group
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteGroupDialog(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete Group
        </MenuItem>
      </Menu>

      <Dialog
        open={renameGroupDialog}
        onClose={() => setRenameGroupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Rename Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            label="Group name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRenameGroupDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleRenameGroup}
            variant="contained"
            startIcon={<Edit3 size={16} />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteGroupDialog}
        onClose={() => setDeleteGroupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group "<strong>{selectedGroupForMenu?.name}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Employees in this group will be moved to "Unassigned".
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteGroupDialog(false);
              handleCloseGroupMenu();
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteGroup}
            variant="contained"
            color="error"
            startIcon={<Trash2 size={16} />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <EmployeeForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSave={() => { setOpenForm(false); fetchData(); }}
        selected={selected}
      />
    </Box>
  );
}